import React, { useState, useEffect } from 'react';
import { Users, Trophy, Ticket, RefreshCw, AlertTriangle, Mail, Calendar, Gift } from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from '../../utils/toastWithSound';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminReferrals = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const fetchWinners = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/referrals/admin/winners`);
      setWinners(response.data);
    } catch (error) {
      console.error('Failed to fetch winners:', error);
      // Don't show error toast - empty state will be shown instead
      setWinners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWinners();
  }, []);

  const handleResetCampaign = async () => {
    setResetting(true);
    try {
      const response = await axios.post(`${API}/api/referrals/admin/reset-campaign`);
      toast.success(`Campaign reset! ${response.data.users_reset} users affected.`);
      setShowResetConfirm(false);
      fetchWinners();
    } catch (error) {
      console.error('Failed to reset campaign:', error);
      toast.error('Failed to reset campaign');
    } finally {
      setResetting(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-referrals">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <Gift className="w-7 h-7 text-amber-500" />
            Share & Win Campaign
          </h2>
          <p className="text-muted-foreground mt-1">
            Beheer de maandelijkse 100 XCG Cash actie
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={fetchWinners}
            disabled={loading}
            className="px-4 py-2 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowResetConfirm(true)}
            className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            Reset Campaign
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{winners.length}</p>
              <p className="text-sm text-muted-foreground">Winnaars (3/3)</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {winners.reduce((sum, w) => sum + w.total_tickets, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Totaal Tickets</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {winners.reduce((sum, w) => sum + w.total_signups, 0)}
              </p>
              <p className="text-sm text-muted-foreground">Bonus Registraties</p>
            </div>
          </div>
        </div>
      </div>

      {/* Winners Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground">Deelnemers met 3/3 bezoeken</h3>
          <p className="text-sm text-muted-foreground">Selecteer handmatig een winnaar voor de maandelijkse prijs</p>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : winners.length === 0 ? (
          <div className="p-8 text-center">
            <Trophy className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Nog geen deelnemers met 3/3 bezoeken</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Gebruiker</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Referral Code</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bezoeken</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Registraties</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tickets</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Datum Bereikt</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {winners.map((winner) => (
                  <tr key={winner.user_id} className="hover:bg-secondary/30 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-foreground">{winner.name}</p>
                        <p className="text-xs text-muted-foreground">{winner.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="px-2 py-1 bg-secondary rounded text-sm font-mono text-cyan-500">
                        {winner.referral_code}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 text-green-500 rounded-full text-sm font-medium">
                        <Users className="w-3.5 h-3.5" />
                        {winner.total_visits}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {winner.total_signups > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-500/10 text-cyan-500 rounded-full text-sm font-medium">
                          +{winner.total_signups}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500/10 text-amber-500 rounded-full text-sm font-bold">
                        <Ticket className="w-3.5 h-3.5" />
                        {winner.total_tickets}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(winner.goal_reached_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <a
                        href={`mailto:${winner.email}?subject=Gefeliciteerd! Je hebt gewonnen bij Your Buy Site&body=Beste ${winner.name},%0D%0A%0D%0AGefeliciteerd! Je bent geselecteerd als winnaar van onze Share %26 Win actie!%0D%0A%0D%0AJe hebt 100 XCG Cash gewonnen.%0D%0A%0D%0ANeem contact met ons op om je prijs te claimen.%0D%0A%0D%0AGroeten,%0D%0AYour Buy Site Team`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Contact
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowResetConfirm(false)} />
          <div className="relative bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-foreground">Campaign Resetten?</h3>
                <p className="text-sm text-muted-foreground">Dit kan niet ongedaan worden gemaakt</p>
              </div>
            </div>
            
            <p className="text-muted-foreground mb-6">
              Dit zal alle referral voortgang van alle gebruikers verwijderen. Hun referral codes blijven behouden. 
              Gebruik dit alleen aan het begin van een nieuwe campagne periode.
            </p>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-secondary text-foreground rounded-lg hover:bg-secondary/80 transition-colors font-medium"
              >
                Annuleren
              </button>
              <button
                onClick={handleResetCampaign}
                disabled={resetting}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {resetting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Ja, Reset Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReferrals;
